import { useEffect, useState } from 'react'
import { CallProvider } from './CallProvider'
import { ConferenceCallRoom } from './ConferenceCallRoom'
import { WaitingRoomGuestView } from './waitingRoomPanels'
import type { ConferenceRoomGateProps, JoinSession } from './ConferenceRoomGate.types'
import { humanizeJoinError, validateJoinSession } from './conferenceRoomGate.utils'
import { api, ApiError } from '../../../../api/client'
import type { WaitingSession } from '../../../../api/client'
import './conference-gate.css'

type GateState = {
  meetingId: string
  loading: boolean
  session?: JoinSession
  error?: string
  waitingSession?: WaitingSession
}

/**
 * Before minting a join token, resolve host-vs-guest via the SAME call the
 * waiting-room host panel uses: `waitingRoom.sessions` 404s anyone who is
 * not the meeting's creator or a role>=80 admin (sessions.ts:19). A 200
 * response IS the host/admin proof; a 404 IS the "you are a guest" signal.
 * This doubles as the authorization probe — no new endpoint. Non-host
 * callers must then `enter()` and wait for admission (issueJoinToken's own
 * admission guard makes this a real gate, not a cosmetic one) before the
 * existing join flow runs unchanged.
 */
export function ConferenceRoomGate({ meetingId, metadata, loadJoinSession, onExit, createProvider, displayName, canDecideApprovals }: ConferenceRoomGateProps) {
  const [state, setState] = useState<GateState>(() => ({ meetingId, loading: true }))
  // Bumped by onAdmitted to force the resolve effect to re-run — without
  // this, admission only flipped `loading` to true with nothing to ever
  // set it back: the effect's dependencies never changed, so the real
  // join (runJoin) never re-fired and an admitted guest hung on the
  // loading spinner forever. Re-running resolve() re-probes enter(),
  // which resumes the now-admitted session and proceeds to runJoin().
  const [resolveTick, setResolveTick] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    const runJoin = () => {
      void loadJoinSession(meetingId, signal)
        .then((result) => {
          if (signal.aborted) return
          if (!result.ok || !result.session) {
            setState({ meetingId, loading: false, error: humanizeJoinError(result.reason) })
            return
          }
          const invalid = validateJoinSession(result.session)
          if (invalid) {
            setState({ meetingId, loading: false, error: humanizeJoinError(invalid) })
            return
          }
          setState({ meetingId, loading: false, session: result.session })
        })
        .catch(() => {
          if (!signal.aborted) setState({ meetingId, loading: false, error: humanizeJoinError('join_failed') })
        })
    }

    const resolve = async () => {
      setState({ meetingId, loading: true })
      try {
        await api.commandCenter.meetings.waitingRoom.sessions(meetingId)
        if (signal.aborted) return
        runJoin()
        return
      } catch (err) {
        if (signal.aborted) return
        if (!(err instanceof ApiError) || err.status !== 404) {
          // Not a clean "you are a guest" signal — fall through to the
          // normal join attempt, which will surface its own honest error.
          runJoin()
          return
        }
      }

      try {
        const res = await api.commandCenter.meetings.waitingRoom.enter(meetingId, displayName)
        if (signal.aborted) return
        if (!res.ok || !res.session) {
          setState({ meetingId, loading: false, error: humanizeJoinError(res.reason) })
          return
        }
        if (res.session.status === 'admitted') { runJoin(); return }
        setState({ meetingId, loading: false, waitingSession: res.session })
      } catch {
        if (!signal.aborted) setState({ meetingId, loading: false, error: humanizeJoinError('join_failed') })
      }
    }

    void resolve()
    return () => controller.abort()
  }, [meetingId, loadJoinSession, displayName, resolveTick])

  const current = state.meetingId === meetingId ? state : { meetingId, loading: true }

  if (current.waitingSession) {
    return (
      <WaitingRoomGuestView
        session={current.waitingSession}
        onAdmitted={() => { setState({ meetingId, loading: true }); setResolveTick((t) => t + 1) }}
        onTerminal={(reason) => setState({ meetingId, loading: false, error: humanizeJoinError(reason) })}
      />
    )
  }

  if (current.loading || current.error || !current.session) {
    return (
      <div className="sq-conference-gate" role="status" aria-live="polite">
        <div className="sq-conference-gate__card">
          <span className="sq-conference-gate__badge">{current.loading ? 'SECURING ROOM' : 'CANNOT JOIN'}</span>
          <p>{current.loading ? 'Requesting a short-lived, server-scoped meeting token.' : current.error}</p>
          {!current.loading ? <button type="button" onClick={onExit}>Back to Conference Calls</button> : null}
        </div>
      </div>
    )
  }

  return (
    <CallProvider
      input={{ roomUrl: current.session.roomUrl, token: current.session.token, displayName }}
      createProvider={createProvider}
      autoPreview={false}
    >
      <ConferenceCallRoom {...metadata} meetingId={meetingId} canDecideApprovals={canDecideApprovals} onExit={onExit} />
    </CallProvider>
  )
}
